import React, { useState } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { getErrorMessage } from '../utils/helpers'
import { useAuth } from '@/hooks/useAuth'
import { apiClient } from '@/utils/api'

export default function GoogleOnlyLoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const error = searchParams.get('error')
  const { loginFromToken } = useAuth()
  // Check for returnTo from ProtectedRoute state, or sessionStorage (preserved across OAuth redirect)
  const stateReturnTo = (location.state as any)?.returnTo
  const storedReturnTo = sessionStorage.getItem('login_return_to')
  const returnTo = stateReturnTo || storedReturnTo || '/dashboard'

  // If we got a returnTo from state, persist it for OAuth redirects
  React.useEffect(() => {
    if (stateReturnTo) {
      sessionStorage.setItem('login_return_to', stateReturnTo)
    }
  }, [stateReturnTo])
  const [passwordLoginEnabled, setPasswordLoginEnabled] = useState(false)
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)

  React.useEffect(() => {
    // Check for OAuth token in URL (from centralized auth service)
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('token')
    const googleLogin = urlParams.get('google_login')
    const appleLogin = urlParams.get('apple_login')
    const microsoftLogin = urlParams.get('microsoft_login')

    // Handle OAuth login from any provider
    if (token && (googleLogin || appleLogin || microsoftLogin)) {
      const provider = googleLogin ? 'Google' : appleLogin ? 'Apple' : 'Microsoft'

      // Clear the token from URL first
      window.history.replaceState({}, document.title, window.location.pathname)

      console.log(`Processing ${provider} OAuth token from URL`)

      // Validate token with backend and get user info
      loginFromToken(token).then((result) => {
        if (result.success) {
          toast.success(`${provider} login successful!`)
          sessionStorage.removeItem('login_return_to')
          navigate(returnTo)
        } else {
          toast.error(result.error || 'Authentication failed. Please try again.')
        }
      }).catch((err) => {
        console.error(`${provider} login error:`, err)
        toast.error('Authentication failed. Please try again.')
      })
      return
    }

    // Backward compatibility: check for old auth_token parameter (Google only)
    const authToken = urlParams.get('auth_token')
    if (authToken) {
      // Clear the token from URL first
      window.history.replaceState({}, document.title, window.location.pathname)

      // Validate token with backend and get user info
      loginFromToken(authToken).then((result) => {
        if (result.success) {
          toast.success('Login successful!')
          sessionStorage.removeItem('login_return_to')
          navigate(returnTo)
        } else {
          toast.error(result.error || 'Authentication failed. Please try again.')
        }
      }).catch((err) => {
        console.error('Login error:', err)
        toast.error('Authentication failed. Please try again.')
      })
      return
    }

    // Show error if present
    if (error === 'google_auth_failed' || error === 'token_exchange_failed' || error === 'user_info_failed') {
      toast.error('Authentication failed. Please try again.')
    } else if (error === 'unauthorized_user' || error === 'invalid_state') {
      toast.error('Access denied. Please try again.')
    }
  }, [error, navigate, loginFromToken])
  
  // Check if this family has password login enabled
  React.useEffect(() => {
    apiClient.get('/api/family/kid-password-auth')
      .then((data: any) => {
        if (data?.enabled) {
          setPasswordLoginEnabled(true)
        }
      })
      .catch(() => {
        // Not available or not enabled, that's fine
      })
  }, [])

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!username || !password) {
      toast.error('Please enter username and password')
      return
    }
    setLoginLoading(true)
    try {
      const response = await apiClient.post('/api/auth/login', {
        username: username.toLowerCase(),
        password
      })
      if (response.token) {
        localStorage.setItem('auth_token', response.token)
        const fullUser = await apiClient.getCurrentUser()
        localStorage.setItem('user', JSON.stringify(fullUser))
        toast.success('Login successful!')
        sessionStorage.removeItem('login_return_to')
          navigate(returnTo)
      }
    } catch (err: any) {
      toast.error(getErrorMessage(err, 'Invalid username or password'))
    } finally {
      setLoginLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    try {
      // Redirect to centralized auth service (like mobile app does)
      // Auth service doesn't require family context
      const returnTo = window.location.origin
      const authServiceURL = `https://auth.housepoints.ai/api/auth/google?return_to=${encodeURIComponent(returnTo)}`
      window.location.href = authServiceURL
    } catch (error) {
      console.error('OAuth init error:', error)
      toast.error('Failed to start Google authentication')
    }
  }

  const handleAppleLogin = async () => {
    try {
      const returnTo = window.location.origin
      const authServiceURL = `https://auth.housepoints.ai/api/auth/apple?return_to=${encodeURIComponent(returnTo)}`
      window.location.href = authServiceURL
    } catch (error) {
      console.error('Apple OAuth init error:', error)
      toast.error('Failed to start Apple authentication')
    }
  }

  const handleMicrosoftLogin = async () => {
    try {
      const returnTo = window.location.origin
      const authServiceURL = `https://auth.housepoints.ai/api/auth/microsoft?return_to=${encodeURIComponent(returnTo)}`
      window.location.href = authServiceURL
    } catch (error) {
      console.error('Microsoft OAuth init error:', error)
      toast.error('Failed to start Microsoft authentication')
    }
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="card text-center mb-8">
          <a href="https://housepoints.ai" className="block">
            <h1 className="text-4xl font-bold gradient-text mb-4 hover:opacity-80 transition-opacity cursor-pointer">
              🏠 HousePoints
            </h1>
          </a>
          <p className="text-lg text-gray-600">
            Secure login for family members only
          </p>
        </div>

        {/* Google Login Card */}
        <div className="card">
          <div className="text-center mb-6">
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold mb-2">Welcome Back!</h2>
            <p className="text-gray-600">Sign in with your family Google account</p>
          </div>
          
          <div className="space-y-3">
            {/* Google Sign In */}
            <button
              onClick={handleGoogleLogin}
              className="w-full bg-white border border-gray-300 rounded-lg px-6 py-3 flex items-center justify-center space-x-3 hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="font-medium text-gray-700 text-lg">Continue with Google</span>
            </button>

            {/* Apple Sign In */}
            <button
              onClick={handleAppleLogin}
              className="w-full bg-black text-white border border-black rounded-lg px-6 py-3 flex items-center justify-center space-x-3 hover:bg-gray-900 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              <span className="font-medium text-lg">Continue with Apple</span>
            </button>

            {/* Microsoft Sign In */}
            <button
              onClick={handleMicrosoftLogin}
              className="w-full bg-white border border-gray-300 rounded-lg px-6 py-3 flex items-center justify-center space-x-3 hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <svg className="w-6 h-6" viewBox="0 0 23 23">
                <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
                <path fill="#f35325" d="M1 1h10v10H1z"/>
                <path fill="#81bc06" d="M12 1h10v10H12z"/>
                <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                <path fill="#ffba08" d="M12 12h10v10H12z"/>
              </svg>
              <span className="font-medium text-gray-700 text-lg">Continue with Microsoft</span>
            </button>
          </div>

          {passwordLoginEnabled && (
            <div className="mt-6 border-t border-gray-200 pt-4">
              {!showPasswordForm ? (
                <button
                  onClick={() => setShowPasswordForm(true)}
                  className="w-full text-center text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  Sign in with username and password
                </button>
              ) : (
                <form onSubmit={handlePasswordLogin} className="space-y-3">
                  <div>
                    <input
                      type="text"
                      placeholder="Username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                      autoCapitalize="none"
                      autoCorrect="off"
                    />
                  </div>
                  <div>
                    <input
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loginLoading}
                    className="w-full bg-indigo-600 text-white rounded-lg px-4 py-2.5 font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 text-sm"
                  >
                    {loginLoading ? 'Signing in...' : 'Sign In'}
                  </button>
                </form>
              )}
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Only authorized family members can access this system
            </p>
          </div>
        </div>
        
        {/* Info Section */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            Having trouble? Contact your system administrator.
          </p>
        </div>
      </div>
    </div>
  )
}